import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { isAndroidApp } from "@/lib/platform";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";

const SUPPORT_EMAIL = "support@fitauraai.com";

const Privacy = () => {
  const navigate = useNavigate();
  const onAndroid = isAndroidApp();

  // SEO handled by <SEO /> component below.

  return (
    <>
      <SEO />
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
      </div>
      <header className="sticky top-0 z-30 border-b border-white/5 bg-background/85 px-5 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            aria-label="Back"
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
            <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
          </span>
          <div className="min-w-0">
            <span className="font-display block text-[15px] font-bold leading-none tracking-tight">
              FitAura<span className="text-primary"> AI</span>
            </span>
            <span className="font-display mt-1 block truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Privacy Policy
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl px-5 py-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_0_24px_-6px_hsl(var(--primary))]">
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
              — PRIVACY
            </span>
            <h2 className="font-display mt-0.5 text-2xl font-bold uppercase tracking-tight">Your data, explained.</h2>
            <p className="text-xs text-muted-foreground">Last updated: April 19, 2026</p>
          </div>
        </div>

        <Card className="space-y-6 p-5 shadow-soft">
          <section>
            <h3 className="mb-2 text-base font-bold">1. Who we are</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              FitAura AI ("we", "us") — operated by Gayam Venkateswarlu Reddy — provides an AI-powered virtual try-on and outfit
              styling experience. This policy explains what data we collect, why we collect
              it, and how you stay in control. By using FitAura AI you agree to this policy.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">2. What we collect</h3>
            <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
              <li>
                <span className="font-semibold text-foreground">Account info:</span> email,
                display name, and an authentication identifier from your sign-in provider
                (e.g. Google).
              </li>
              <li>
                <span className="font-semibold text-foreground">Profile preferences:</span>{" "}
                gender, body type, skin tone, height, size, favourite styles and colours —
                all optional, used to personalise outfit suggestions.
              </li>
              <li>
                <span className="font-semibold text-foreground">Photos you upload:</span>{" "}
                your selfie / body photo and any garment images you choose to try on.
              </li>
              <li>
                <span className="font-semibold text-foreground">Generated images:</span>{" "}
                the try-on and outfit images our AI creates from your inputs.
              </li>
              <li>
                <span className="font-semibold text-foreground">Usage data:</span> credits
                used, purchase records, and basic logs needed to keep the service reliable
                and secure.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">3. Photo uploads &amp; AI processing</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              When you upload a photo, it is securely transmitted to our backend (Lovable
              Cloud, powered by Supabase) and stored in a private storage bucket that only
              you can access through authenticated, time-limited signed URLs.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              To generate a try-on or outfit, we send your photo and selected garment image
              to <span className="font-semibold text-foreground">Google Gemini</span> via
              the Lovable AI Gateway for processing. We do <span className="font-semibold">not</span>
              {" "}use your photos to train AI models.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">Generated images are kept in the cloud for only 24 hours</span>{" "}
              and then permanently deleted by an automated sweeper. On Android, results are
              auto-saved to your device gallery so you keep them after the cloud copy expires.
              Marking a result as a favorite does <span className="font-semibold">not</span>{" "}
              extend its cloud lifetime — the 24-hour rule applies to every image.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              We never sell your photos. We never share them with advertisers. We never
              show your photos to other users.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">4. Payments</h3>
            {onAndroid ? (
              <>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  All credit purchases in the app are processed by{" "}
                  <span className="font-semibold text-foreground">Google Play Billing</span>.
                  Google Play handles checkout, card processing, tax and bank settlement.
                  We only receive an order reference and the credit amount to add to your
                  balance — we never see or store your card details, billing address, or
                  payment credentials.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Your payment data is governed by{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Google's Privacy Policy
                  </a>. Manage your purchases, subscriptions and refund requests from your{" "}
                  <a
                    href="https://play.google.com/store/account/orderhistory"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Google Play order history
                  </a>.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Credit purchases are processed by one of two payment providers depending on
                  your region:
                </p>
                <ul className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  <li>
                    <span className="font-semibold text-foreground">India:</span> Razorpay handles
                    checkout, card processing and bank settlement. We store the order reference
                    and credit ledger entry only.
                  </li>
                  <li>
                    <span className="font-semibold text-foreground">International:</span> Our
                    online reseller acts as Merchant of Record and is the data controller for
                    the payment transaction itself (cardholder data, billing address, tax
                    jurisdiction). Their privacy policy is shown at checkout.
                  </li>
                </ul>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  In all cases, card numbers and payment credentials are handled entirely by the
                  payment provider and never touch our servers.
                </p>
              </>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">5. Children</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              FitAura AI is not intended for children under 13. Do not upload photos of
              children. If you believe a child has used the service, contact us and we will
              delete the data.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">6. Your rights &amp; data deletion</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              You can review and update your profile at any time inside the app. To delete
              your account along with all uploaded photos, generated results, and profile
              data, email us at the address below from your registered email. We honour
              deletion requests within 30 days.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">7. Security</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Data is encrypted in transit (HTTPS) and at rest. Access to your photos and
              profile is protected by row-level security policies and signed URLs with
              short expirations.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">8. Advertising</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              On our website we may display ads served by Google AdSense to non-paying
              users. Google and its partners use cookies and similar technologies to
              serve ads based on your prior visits to this and other websites. You can
              opt out of personalized advertising by visiting{" "}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Google Ads Settings
              </a>
              . Paid users do not see ads.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">9. Changes to this policy</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              If we change this policy materially, we will update the "Last updated" date
              and, where appropriate, notify you in the app.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">9. Contact</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Questions, requests, or deletion requests:
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=FitAura%20AI%20privacy%20request`}
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-accent"
            >
              <Mail className="h-4 w-4 text-primary" />
              {SUPPORT_EMAIL}
            </a>
          </section>
        </Card>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-6" />
        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="underline-offset-2 hover:underline">
            Back to FitAura AI
          </Link>
        </p>
      </main>
    </div>
    </>
  );
};

export default Privacy;
